/**
 * Turning a flat list of things you follow into the home screen.
 *
 * This is where the app's opinion lives, so it is pure and tested. Every item
 * lands in exactly one place, and the ordering of that decision matters: a
 * book that is out *and* already read must disappear rather than reappear at
 * the bottom of the list forever.
 */

import { compareDates, rangeStart } from '../../poller/dates.js';
import type { DateSlot, SlateDate, Snapshot } from '../../poller/types.js';
import { formatLabel, formatsSentence, wantsFormat } from './formats.js';
import { hasPassedLocally } from './localdate.js';
import { formatDate, monthsBetween } from './format.js';
import type { ChangeCard, HomeModel, SpineRow, WaitingRow, WatchEvent, WatchItem } from './model.js';
import type { Details } from '../../details/types.js';
import type { SearchResult } from '../../search/types.js';

type Releases = Partial<Record<DateSlot, SlateDate | null>>;

/**
 * The dates as this particular subscriber should see them.
 *
 * A film's date is genuinely different in different countries, so where the
 * poller has stored the region they asked for, those dates win. Slots the
 * region says nothing about fall through to the shared answer rather than
 * disappearing — a missing Irish disc date does not mean there is no disc.
 */
export function regionalReleases(item: WatchItem): Releases {
  const snapshot = item.snapshot;
  if (!snapshot || snapshot.kind !== 'title') return {};
  if (!item.region) return snapshot.releases;

  const local = snapshot.regions?.[item.region];
  if (!local) return snapshot.releases;

  return { ...snapshot.releases, ...local };
}

/**
 * The dates that decide where this lands on the home screen.
 *
 * Choosing formats is a filter, not a note: someone who only listens is not
 * finished waiting when the hardback arrives. Everything they did not choose
 * is still *shown* — the detail screen lists every format there is — but it
 * cannot move the item into "out now" or occupy its place on the spine.
 */
export function wantedReleases(item: WatchItem): Releases {
  const releases = regionalReleases(item);
  if (item.formats.length === 0) return releases;

  const kept: Releases = {};
  for (const [slot, date] of Object.entries(releases)) {
    if (wantsFormat(item.formats, slot as DateSlot)) kept[slot as DateSlot] = date;
  }
  return kept;
}

/** Below this, a gap isn't worth drawing — things are simply consecutive. */
const GAP_MONTHS = 3;

export interface DeriveInput {
  items: readonly WatchItem[];
  events: readonly WatchEvent[];
  lastSeenAt: string | null;
  now: Date;
}

export function deriveHome({ items, events, lastSeenAt, now }: DeriveInput): HomeModel {
  const byId = new Map(items.map((i) => [i.watchableId, i]));

  const changes: ChangeCard[] = events
    .filter((e) => !e.seen)
    .map((e) => {
      const item = byId.get(e.watchableId);
      return item ? { event: e, item } : null;
    })
    .filter((c): c is ChangeCard => c !== null)
    .sort((a, b) => b.event.detectedAt.localeCompare(a.event.detectedAt));

  const outNow: WatchItem[] = [];
  const dated: DatedEntry[] = [];
  const undated: WatchItem[] = [];

  for (const item of items) {
    const releases = wantedReleases(item);
    const pending = soonestPending(releases, now);

    if (pending) {
      dated.push({ item, slot: pending.slot, date: pending.date });
      continue;
    }

    // Every date it has is fully behind us, or it has none at all.
    if (hasAnyDate(releases)) {
      // Read or watched already: it has done its job and leaves the screen.
      if (!item.consumedAt) outNow.push(item);
      continue;
    }

    undated.push(item);
  }

  dated.sort((a, b) => compareDates(a.date, b.date));

  return {
    outNow: outNow.sort((a, b) => a.title.localeCompare(b.title)),
    changes,
    spine: buildSpine(dated),
    waiting: buildWaiting(undated, now),
    lastSeenAt,
  };
}

/**
 * The soonest date that has not *finished* happening.
 *
 * Deliberately not `soonestUpcoming` from the poller, which counts a date as
 * gone the moment its range opens — right for deciding when to poll next,
 * wrong here. A book known only as "2026", read in July 2026, is neither
 * strictly upcoming nor demonstrably out; the poller can treat it as started,
 * but the app must not put it in "ready to read" on the strength of a year.
 *
 * `hasPassedLocally`, not the poller's `hasPassed`: an air date is a calendar
 * date, and the calendar that matters on a screen is the reader's own —
 * judged in UTC, "4 Aug" stayed pending until mid-morning on the 5th for
 * anyone east of Greenwich.
 */
function soonestPending(
  releases: Releases,
  now: Date,
): { slot: DateSlot; date: SlateDate } | null {
  let best: { slot: DateSlot; date: SlateDate } | null = null;
  for (const [slot, date] of Object.entries(releases)) {
    if (!date || hasPassedLocally(date, now)) continue;
    if (best === null || compareDates(date, best.date) < 0) {
      best = { slot: slot as DateSlot, date };
    }
  }
  return best;
}

function hasAnyDate(releases: Releases): boolean {
  return Object.values(releases).some((d) => d != null);
}

/**
 * The one date to put at the top of a thing's own page.
 *
 * Not simply the primary slot: someone waiting for the audiobook should see
 * the audiobook date in the big type, not the hardback's. Falls back to the
 * earliest date in any format they *did* ask for, so a book with no
 * publication date but a dated ebook says so rather than "no date".
 */
export function headlineRelease(item: WatchItem): { slot: DateSlot; date: SlateDate } | null {
  const releases = wantedReleases(item);
  if (releases.primary) return { slot: 'primary', date: releases.primary };

  let best: { slot: DateSlot; date: SlateDate } | null = null;
  for (const [slot, date] of Object.entries(releases)) {
    if (!date) continue;
    if (best === null || compareDates(date, best.date) < 0) {
      best = { slot: slot as DateSlot, date };
    }
  }
  return best;
}

/**
 * Why something is still waiting when a version of it is already out.
 *
 * A filter you cannot see is indistinguishable from a bug: if the hardback is
 * on the shelves and this app says "no date", the app looks broken unless it
 * admits, in the same breath, that you asked to hear about the audiobook.
 */
export function formatNote(item: WatchItem, now: Date): string | null {
  const sentence = formatsSentence(item.formats, item.medium);
  if (!sentence) return null;

  const ignored = Object.entries(regionalReleases(item))
    .filter(([slot, date]) => date != null && !wantsFormat(item.formats, slot as DateSlot))
    .filter(([, date]) => hasPassedLocally(date as SlateDate, now))
    .map(([slot]) => formatLabel(slot as DateSlot, item.medium).toLowerCase());

  if (ignored.length === 0) return sentence;
  return `${sentence} · ${ignored[0]} is out`;
}

/**
 * The spine, with the quiet stretches drawn in. Seeing that there is a clump
 * in March and then nothing until autumn is the point — it tells you this is
 * a good month to start something long.
 */
interface DatedEntry {
  item: WatchItem;
  slot: DateSlot;
  date: SlateDate;
}

function buildSpine(dated: readonly DatedEntry[]): SpineRow[] {
  const rows: SpineRow[] = [];

  dated.forEach((entry, index) => {
    const previous = dated[index - 1];
    if (previous) {
      const months = monthsBetween(previous.date, entry.date);
      if (months >= GAP_MONTHS) rows.push({ kind: 'gap', months });
    }
    rows.push({
      kind: 'entry',
      item: entry.item,
      slot: entry.slot,
      date: entry.date,
      next: index === 0,
    });
  });

  return rows;
}

function buildWaiting(items: readonly WatchItem[], now: Date): WaitingRow[] {
  return items
    .map((item): WaitingRow => {
      const placeholder = placeholderFor(item, now);
      return placeholder ?? { kind: 'real', item, staleness: stalenessOf(item, now) };
    })
    .sort((a, b) => a.item.title.localeCompare(b.item.title));
}

/**
 * A series that hasn't finished implies a next book, whether or not anyone
 * has announced one. Those are the entries people most want and most easily
 * forget, so they are shown as an explicit slot rather than left implied.
 *
 * Authors deliberately get nothing here: a series can count to "Book 5", an
 * author can only promise "something, someday", which is noise.
 */
export function placeholderFor(item: WatchItem, now: Date): WaitingRow | null {
  if (item.kind !== 'series') return null;
  if (!item.showPlaceholder) return null;
  if (item.completion === 'completed') return null;

  const next = (item.knownPositions ?? 0) + 1;
  if (next < 2) return null;

  const label = `${item.title}, Book ${next}`;
  return { kind: 'placeholder', item, label, note: placeholderNote(item, next, now) };
}

/** What `SeriesFan` draws: the covers, front first, and whether a blank sits behind. */
export interface SeriesFan {
  covers: string[];
  blank: boolean;
}

/** At three the peeks stop being distinguishable and start being a smudge. */
const FAN_MAX = 3;

/**
 * A series' thumbnail, which has to say "a series" rather than "a book".
 *
 * Hardcover has no cover for a series at all, so `image_url` is its earliest
 * book's, borrowed. One borrowed cover is an honest picture of the wrong thing:
 * it describes a book on a row that stands for eight of them. The books' own
 * covers are already loaded — nothing here fetches anything.
 *
 * Returns null when there is nothing to fan, and the caller falls back to the
 * ordinary thumbnail. That covers the series with no details yet, and the
 * finished one-book series, both of which are just a cover.
 */
export function seriesFan(item: WatchItem, now: Date): SeriesFan | null {
  if (item.kind !== 'series') return null;

  const works = (item.details?.works ?? []).slice().sort(byPosition);

  // A reissue and its original share artwork, and two identical covers read as
  // a rendering fault rather than as two books.
  const covers = [...new Set(works.map((w) => w.imageUrl).filter(isUrl))].slice(0, FAN_MAX);

  // Gated on the placeholder rather than on a count, so the picture cannot
  // contradict the words: the row that says "Book 5 — not announced" is the
  // same condition that draws the empty slot. Two covers already read as a
  // series, so the blank would be adding nothing but a third shape.
  const blank = covers.length < 2 && placeholderFor(item, now) !== null;

  // One layer is a thumbnail, not a fan.
  if (covers.length + (blank ? 1 : 0) < 2) return null;

  return { covers, blank };
}

function isUrl(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** Unpositioned works sort last: an unplaced book is not book one. */
function byPosition(a: { position?: number }, b: { position?: number }): number {
  return (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER);
}

function placeholderNote(item: WatchItem, next: number, now: Date): string {
  // A stated plan is a claim, not a fact, and is labelled as one. Bare
  // fractions would read as certainty nobody has.
  if (item.expectedTotal && next <= item.expectedTotal) {
    return `not announced · ${next} of ${item.expectedTotal}, author's estimate`;
  }

  // Needs no source at all: the gaps between the published books say whether
  // one is due, and cover series nobody has documented.
  const expected = expectedYear(item, now);
  if (expected) return `not announced · next expected ${expected}`;

  const stale = stalenessOf(item, now);
  return stale ? `not announced · ${stale}` : 'not announced yet';
}

/**
 * When the next one would land if the series keeps its rhythm. Deliberately
 * a year rather than a date: this is arithmetic on a pattern, not a schedule.
 */
export function expectedYear(item: WatchItem, now: Date): number | null {
  if (!item.medianGapDays || item.medianGapDays <= 0) return null;
  const latest = latestWork(item);
  if (!latest) return null;

  const due = new Date(rangeStart(latest).getTime() + item.medianGapDays * 86_400_000);
  // Already overdue — saying "expected 2025" in 2026 would be silly.
  if (due.getTime() < now.getTime()) return now.getUTCFullYear();
  return due.getUTCFullYear();
}

/**
 * "nothing since 2011". The line that separates a series merely pending from
 * one that is, realistically, never — without ever claiming it has ended.
 */
export function stalenessOf(item: WatchItem, now: Date): string | null {
  const latest = latestWork(item);
  if (!latest) return null;

  const years = now.getUTCFullYear() - Number(latest.value.slice(0, 4));
  if (years < 3) return null;
  return `nothing since ${latest.value.slice(0, 4)}`;
}

function latestWork(item: WatchItem): SlateDate | null {
  const snapshot = item.snapshot;
  if (!snapshot || snapshot.kind !== 'collection') return null;

  const dates = snapshot.works
    .map((w) => w.releaseDate)
    .filter((d): d is SlateDate => d != null)
    .sort(compareDates);

  return dates.length ? (dates[dates.length - 1] as SlateDate) : null;
}

/**
 * What the home screen is: two questions, and nothing else.
 *
 * "Can I start something?" and "has anything moved?" — each with a heading in
 * the display face, each absent entirely when the answer is nothing. A
 * heading over an empty list is a question asked and not answered.
 */
export interface HomeSummary {
  ready: number;
  updates: number;
  /** Both empty. The screen says "No changes." and stops. */
  quiet: boolean;
  /**
   * Counts spelled out under the headings, for when the two lists together
   * are too long to take in at a glance. Below that, the lists *are* the
   * summary and repeating their lengths is noise.
   */
  counts: string | null;
}

const COUNT_THRESHOLD = 5;

export function homeSummary(model: HomeModel): HomeSummary {
  const ready = model.outNow.length;
  const updates = model.changes.length;
  const both = ready > 0 && updates > 0;

  return {
    ready,
    updates,
    quiet: ready === 0 && updates === 0,
    counts:
      both && ready + updates > COUNT_THRESHOLD
        ? `${ready} ready · ${updates} ${updates === 1 ? 'update' : 'updates'}`
        : null,
  };
}

/**
 * The Slate page's one line: what the wait actually looks like. Answers "is
 * anything close?" before the timeline has to be read.
 */
export function slateSummary(model: HomeModel, now: Date): string {
  const next = model.spine.find((row) => row.kind === 'entry');
  const waiting = model.waiting.length;

  if (!next) {
    return waiting === 1 ? 'One thing, no date yet.' : `${capitalise(countWord(waiting))} things, no date yet.`;
  }

  const months = monthsUntil(next.date, now);
  const when =
    months <= 0 ? 'this month' : months === 1 ? 'next month' : `in ${countWord(months)} months`;
  return `Next up ${when}.`;
}

/**
 * The reader's calendar on both sides, which is the only way the two agree.
 *
 * `rangeStart` builds a UTC instant out of a calendar value, so its UTC month
 * *is* the month printed on the date — but `now` is a real instant, and its
 * UTC month is not necessarily the month the reader is in. Mixing the two put
 * Sydney in February at ten in the morning on 1 March, so this line said "next
 * month" about something releasing in the reader's current one, on the same
 * screen whose Ready/pending split had already rolled over.
 */
function monthsUntil(date: SlateDate, now: Date): number {
  const start = rangeStart(date);
  return Math.max(
    0,
    (start.getUTCFullYear() - now.getFullYear()) * 12 + (start.getUTCMonth() - now.getMonth()),
  );
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

function countWord(n: number): string {
  return (n < WORDS.length ? WORDS[n] : String(n)) as string;
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export { formatDate };

/**
 * A search result, dressed as the thing it would become.
 *
 * The peek screen renders the same components the item page does, so the thing
 * they render has to be the same shape. Everything that belongs to a
 * subscription is empty rather than invented: no ids, no chosen formats, no
 * region, nothing consumed.
 *
 * The rule this obeys is that a peek may never show something the item page
 * won't — which is why `medianGapDays` stays null despite the snapshot
 * carrying everything `medianGapDays()` would need. Nothing writes that column
 * yet, so an added item answers "Next expected" with "unknown", and a peek that
 * answered would be the promise broken in the direction nobody expects.
 *
 * Where details and the search result disagree, details wins — the same
 * precedence `storeDetails` uses — except that a missing value never blanks one
 * the search result supplied. A cover found here fills a gap; it does not open
 * one.
 */
export function peekItem(
  result: SearchResult,
  snapshot: Snapshot | null,
  details: Details | null,
): WatchItem {
  const collection = snapshot?.kind === 'collection' ? snapshot : null;

  return {
    subscriptionId: '',
    watchableId: '',
    source: result.source,
    sourceId: result.sourceId,
    kind: result.kind,
    medium: result.medium,

    title: result.title,
    byline: result.byline,
    imageUrl: details?.imageUrl ?? result.imageUrl,
    description: details?.description ?? null,
    descriptionSource: details?.description ? (details.descriptionSource ?? 'own') : 'own',
    tags: details?.tags?.length ? details.tags : result.tags,
    rating: details?.rating ?? null,

    snapshot,
    details,
    completion: collection?.completion ?? null,
    knownPositions: collection?.knownPositions ?? null,
    expectedTotal: null,
    expectedTotalNote: null,
    medianGapDays: null,

    consumedAt: null,
    showPlaceholder: true,

    siteUrl: null,
    siteCursor: null,
    siteSeenAt: null,

    formats: [],
    region: null,
  };
}
